/**
 * Share counting (v1) — the client half.
 *
 * Every share surface in this modal already worked; none of them were ever
 * recorded, which is why "Most Shared" on Discover and a shares term in the
 * Trending formula were both impossible. This posts one tiny event after a
 * share action succeeds.
 *
 * Two rules govern everything here:
 *
 * 1. **It must never affect the user's share.** The user's actual outcome — the
 *    link is on their clipboard, the PDF downloaded, the Facebook window opened
 *    — has already happened by the time this fires. Analytics failing must not
 *    produce a toast, an error boundary, an unhandled rejection, or a delay. So
 *    every call is fire-and-forget and every failure is swallowed.
 *
 * 2. **It must never invent a share.** A record is only sent when the action
 *    genuinely completed. A copy that threw, or a private wheel where the copy
 *    was refused, sends nothing.
 *
 * BACKEND DEPENDENCY — DEPLOY ORDER IS LOAD-BEARING: this calls
 * `POST /api/v2/wheel/{id}/record-share/`, added in Backend PR #1599. That PR
 * must be merged AND deployed before this ships, or every call 404s. A 404 is
 * harmless here by construction (rule 1), but it also means nothing is counted.
 */
import API from "../../services/request";
import { isValidWheelId } from "../ShareVideoModal/shareVideo";

/**
 * The share surfaces this modal offers, as the wire slugs the server accepts.
 * Readable strings rather than the server's integers, so a shipped bundle can
 * never be silently corrupted by a later renumbering of the enum.
 *
 * Kept as a frozen object so a typo (`SHARE_TYPES.FACEBOK`) is `undefined` at
 * the call site rather than a plausible-looking string that the server rejects
 * and the user never hears about.
 */
export const SHARE_TYPES = Object.freeze({
  COPY_LINK: "copy_link",
  QR_SVG: "qr_svg",
  QR_PNG: "qr_png",
  PDF: "pdf",
  EMBED_IFRAME: "embed_iframe",
  EMBED_SCRIPT: "embed_script",
  VIDEO: "video",
  FACEBOOK: "facebook",
  PINTEREST: "pinterest",
  LINKEDIN: "linkedin",
  X: "x",
  MESSENGER: "messenger",
  WHATSAPP: "whatsapp",
  // The native OS share sheet (navigator.share) isn't its own slug on the
  // server — it fans out to whichever app the user picks, which this client
  // never learns. The server's catch-all bucket for exactly this case.
  OTHER: "other",
});

const KNOWN_SHARE_TYPES = new Set(Object.values(SHARE_TYPES));

export const recordSharePath = (wheelId) =>
  `/api/v2/wheel/${wheelId}/record-share/`;

/**
 * True when this call is worth putting on the wire at all.
 *
 * Exported so the guard can be asserted directly — a bad id must be rejected
 * BEFORE the URL is built, or a template literal quietly produces
 * `/api/v2/wheel/undefined/record-share/`, the same 404 class Share-as-Video
 * already had to fix.
 */
export const canRecordShare = (wheelId, shareType) =>
  isValidWheelId(wheelId) && KNOWN_SHARE_TYPES.has(shareType);

/**
 * Record one share. Fire-and-forget: always resolves, never rejects, never
 * throws synchronously.
 *
 * @param {number|string} wheelId
 * @param {string} shareType one of SHARE_TYPES
 * @param {{segmentId?: number|string}} [options] segment target, for when
 *   per-Segment sharing ships (the server already accepts it).
 * @returns {Promise<boolean>} whether a request was actually sent and accepted.
 *   Returned for tests and for a future optimistic count — never for control
 *   flow that could block the user.
 */
export function recordShare(wheelId, shareType, options = {}) {
  if (!canRecordShare(wheelId, shareType)) {
    return Promise.resolve(false);
  }
  const body = { share_type: shareType };
  if (options.segmentId != null) {
    body.segment_id = options.segmentId;
  }
  try {
    return API.post(recordSharePath(wheelId), body)
      .then(() => true)
      // Swallowed on purpose. The share already happened; a 404 (backend not
      // deployed yet), a 429 (throttled), or an offline client must never
      // become something the user sees.
      .catch(() => false);
  } catch (e) {
    // A synchronous throw from the client layer (no interceptor, bad config)
    // must not escape into the share handler either.
    return Promise.resolve(false);
  }
}
